/**
 * Dispatching a message through the device's own share sheet.
 *
 * ─── THE GESTURE TRAP ───────────────────────────────────────────────────────
 * `navigator.share()` must be called SYNCHRONOUSLY inside the click handler.
 * Any `await` before it consumes the user activation and iOS Safari throws
 * NotAllowedError. So everything a message needs — portal token, recipient,
 * body text — has to be resolved at RENDER time and sitting in the DOM before
 * the tap. Nothing in this module fetches.
 *
 * The chain, best to worst:
 *   1. navigator.share()  — the real OS sheet, picks any messaging app
 *   2. sms: deep link     — opens Messages with the body prefilled
 *   3. clipboard          — copy the text and tell the user to paste it
 */

export type ShareOutcome =
  /** The OS sheet completed. NOT proof a message was sent. */
  | 'shared'
  /** User dismissed the sheet. Not an error. */
  | 'dismissed'
  /** Opened the SMS composer. */
  | 'sms'
  /** Text is on the clipboard for the user to paste. */
  | 'copied'
  /** Nothing worked. */
  | 'failed';

export interface ShareRequest {
  title?: string;
  text: string;
  url?: string;
  /** Recipient, for the sms: fallback. Share sheets pick their own recipient. */
  phone?: string | null;
}

/**
 * iOS wants `sms:<number>&body=`, everything else wants `sms:<number>?body=`.
 *
 * This is a genuine platform split, not a quirk to normalize away: iOS treats
 * `?` as part of the phone number and silently drops the body.
 */
export function smsHref(text: string, phone?: string | null, isIOS = detectIOS()): string {
  const to = (phone ?? '').replace(/[^\d+]/g, '');
  const sep = isIOS ? '&' : '?';
  return `sms:${to}${sep}body=${encodeURIComponent(text)}`;
}

export function detectIOS(ua: string = navigator.userAgent): boolean {
  // iPadOS 13+ reports itself as a Mac, so the touch check catches it too.
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return /Macintosh/i.test(ua)
    && typeof navigator !== 'undefined'
    && (navigator.maxTouchPoints ?? 0) > 1;
}

/** The full message a share sheet receives: body plus URL when there is one. */
export function composeShareText(req: ShareRequest): string {
  return req.url ? `${req.text} ${req.url}` : req.text;
}

/**
 * Hand a message to the OS. Call this DIRECTLY in a click handler.
 *
 * Resolves with what actually happened so the caller can decide whether to mark
 * the reminder shared. A dismissal is deliberately distinct from a failure —
 * the user changed their mind, nothing is broken, and nothing should be marked.
 */
export async function shareOrFallback(req: ShareRequest): Promise<ShareOutcome> {
  const text = composeShareText(req);

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      // Pass text only, not url: share targets that accept both often send the
      // URL alone and drop the message.
      await navigator.share({ title: req.title, text });
      return 'shared';
    } catch (err) {
      if (isAbort(err)) return 'dismissed';
      // Anything else (NotAllowedError, no share target) falls through.
    }
  }

  try {
    window.location.href = smsHref(text, req.phone);
    return 'sms';
  } catch { /* fall through to clipboard */ }

  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}

function isAbort(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { name?: string }).name === 'AbortError';
}

/**
 * Whether an outcome should mark the reminder as shared.
 *
 * 'sms' counts: the composer opened with the body prefilled, which is as much
 * as the web can observe. 'copied' does not — the text is on the clipboard and
 * may never be pasted anywhere.
 */
export function countsAsShared(outcome: ShareOutcome): boolean {
  return outcome === 'shared' || outcome === 'sms';
}
